import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Chip,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Logout,
  Person,
  Business,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getApiBaseUrl } from '../services/apiService';
import { isSuperAdminUser } from '../utils/superAdmin';
import { formatPlanDisplayLabel, getPlanChipSx, resolveUserPlanType } from '../utils/planDisplay';
import { useTenant } from '../context/TenantContext';
import { DFT_BRAND } from '../styles/brandPalette';

const PROFILE_AVATAR_BG = DFT_BRAND.avatarBg;

const UserProfileDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { organization } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const closeProfileMenu = () => setAnchorEl(null);
    window.addEventListener('app:close-profile-menu', closeProfileMenu);
    return () => window.removeEventListener('app:close-profile-menu', closeProfileMenu);
  }, []);

  useEffect(() => {
    setAnchorEl(null);
  }, [location.pathname]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  if (!user) {
    return null;
  }

  // Get user initials for avatar
  const getInitials = () => {
    const first = user.firstName?.charAt(0).toUpperCase() || '';
    const last = user.lastName?.charAt(0).toUpperCase() || '';
    return `${first}${last}` || 'U';
  };

  const getProfilePictureUrl = () => {
    if (user?.profilePictureUrl) {
      if (user.profilePictureUrl.startsWith('uploads/') || user.profilePictureUrl.startsWith('/uploads/')) {
        // Backend serves uploads under the server context-path `/api`.
        const relativePath = user.profilePictureUrl.replace(/^\/+/, '');
        const base = `${getApiBaseUrl()}/${relativePath}`;
        return `${base}?v=${encodeURIComponent(relativePath)}`;
      }
      return user.profilePictureUrl;
    }
    return null;
  };

  // Capitalize first letter of each word (for display)
  const toTitleCase = (str: string) =>
    str
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ') || str;

  // Capitalize first letter only (for slug/org display)
  const capitalizeFirst = (str: string) =>
    str && str.length ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;

  // Get full name (display with title case)
  const getFullName = () => {
    const raw = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';
    return toTitleCase(raw);
  };

  const planType = resolveUserPlanType(user.planType, organization?.planType);
  const planLabel = formatPlanDisplayLabel(planType);
  const planChipSx = getPlanChipSx(planType);

  const getRoleLabel = () => {
    if (isSuperAdminUser(user)) return 'Super Admin';
    const r = (user.role || 'MEMBER').toUpperCase();
    switch (r) {
      case 'ADMIN':
        return 'Administrator';
      case 'MEMBER':
        return 'Member';
      case 'PROJECT_MANAGER':
        return 'Project Manager';
      case 'ADMIN_MEMBER':
        return 'Admin & Member';
      default:
        return r
          .split('_')
          .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
          .join(' ');
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <ButtonBase
        onClick={handleClick}
        onMouseDown={(event) => {
          if (open) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          borderRadius: 2,
          px: { xs: 0.5, sm: 1 },
          py: 0.5,
          textAlign: 'left',
          color: 'inherit',
          '&:hover': {
            backgroundColor: 'rgba(15, 23, 42, 0.06)',
          },
        }}
      >
        <Avatar
          src={getProfilePictureUrl() || undefined}
          sx={{
            width: 36,
            height: 36,
            bgcolor: PROFILE_AVATAR_BG,
            color: DFT_BRAND.avatarColor,
            fontWeight: 600,
            fontSize: '0.8rem',
          }}
        >
          {getInitials()}
        </Avatar>
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            minWidth: 0,
            gap: 0.125,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, maxWidth: '100%' }}>
            <Typography
              component="span"
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: '0.875rem',
                color: DFT_BRAND.text,
                lineHeight: 1.25,
              }}
            >
              {getFullName()}
            </Typography>
            <KeyboardArrowDown sx={{ fontSize: 18, color: 'text.primary', flexShrink: 0, opacity: 0.85 }} />
          </Box>
          <Typography
            component="span"
            noWrap
            sx={{
              fontSize: '0.7rem',
              fontWeight: 400,
              color: DFT_BRAND.muted,
              lineHeight: 1.2,
            }}
          >
            {getRoleLabel()}
          </Typography>
        </Box>
      </ButtonBase>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            borderRadius: 2,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)',
            mt: 1.5,
            minWidth: 292,
            p: 0.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Section */}
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            borderRadius: 1.5,
            background: 'linear-gradient(135deg, rgba(237, 243, 255, 0.9) 0%, rgba(38, 153, 149, 0.08) 100%)',
            border: `1px solid ${DFT_BRAND.line}`,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={getProfilePictureUrl() || undefined}
              sx={{
                width: 48,
                height: 48,
                bgcolor: PROFILE_AVATAR_BG,
                color: DFT_BRAND.avatarColor,
                fontWeight: 600,
              }}
            >
              {getInitials()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getFullName()}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.75rem',
                }}
              >
                {user.email}
              </Typography>
              {planLabel && (
                <Chip
                  label={planLabel}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    marginTop: 0.5,
                    ...planChipSx,
                    '& .MuiChip-label': {
                      color: planChipSx.color,
                      px: 1,
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Organization Info */}
        {user.organizationSlug && (
          <>
            <MenuItem
              disabled
              sx={{
                borderRadius: 1.25,
                my: 0.25,
                '&.Mui-disabled': {
                  opacity: 1,
                },
              }}
            >
              <ListItemIcon sx={{ color: '#64748b' }}>
                <Business fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Organization"
                secondary={capitalizeFirst(user.organizationSlug)}
                primaryTypographyProps={{ color: '#64748b', fontWeight: 600 }}
                secondaryTypographyProps={{ color: '#0f172a', fontWeight: 600 }}
              />
            </MenuItem>
            <Divider />
          </>
        )}

        {/* Menu Items */}
        <MenuItem
          onClick={handleProfile}
          sx={{
            borderRadius: 1.25,
            my: 0.25,
            '&:hover': {
              backgroundColor: 'rgba(66, 123, 221, 0.1)',
            },
          }}
        >
          <ListItemIcon>
            <Person fontSize="small" sx={{ color: DFT_BRAND.link }} />
          </ListItemIcon>
          <ListItemText
            primary="Profile"
            primaryTypographyProps={{ fontWeight: 600, color: 'text.primary' }}
          />
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleLogout}
          sx={{
            color: 'error.main',
            borderRadius: 1.25,
            my: 0.25,
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
            },
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ fontWeight: 600, color: 'error.main' }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserProfileDashboard;

